import {useNavigate, useParams} from "react-router-dom";
import {useArchitectureDataStore} from "../../data_store/architectureDataStore.ts";
import {useEffect, useState} from "react";
import {
    Button, ButtonDropdown,
    ContentLayout, Header, SpaceBetween, Spinner, Tabs,
} from "@cloudscape-design/components";
import {useAuth} from "react-oidc-context";
import {Endpoint, handleResponse, sendRequest} from "../../utils/api";
import {
    CANVAS_ID,
    DEFAULT_FLOW_STEPS,
    MIN_DESCRIPTION_LENGTH,
    MIN_FLOW_STEPS,
    MAX_FLOW_STEPS
} from "../../constants";
import {ArchitectureUtils} from "../../utils/architecture";
import {DeleteWithConfirmation} from "../../reusable_components/deleteWithConfirmation/deleteWithConfirmation";
import {IaCProject} from "./iac";
import {
    Architecture,
    ArchitectureGenerationErrorFlashbar, ArchitectureSavedFlashbar,
    GeneratingArchitectureFlashbar,
} from "./architecture";
import {Configuration} from "./configuration";


const HeaderActions = ({
                           canvasMode, generatingArchitecture, savingArchitecture,
                           onUpdateClick, onSaveClick, onClearCanvasClick, onDropdownActionClick,
                           flowStepsInvalid, descriptionInvalid,
}) => {
    const updateButtonTitle = generatingArchitecture? 'Recreating architecture' : 'Recreate architecture'
    const saveButtonTitle = savingArchitecture? 'Saving architecture' : 'Save architecture'

    return canvasMode?
        (
            <SpaceBetween direction={'horizontal'} size={'s'}>
                <Button disabled={savingArchitecture || generatingArchitecture} onClick={onClearCanvasClick}>Clear canvas</Button>
                <Button disabled={descriptionInvalid || flowStepsInvalid || savingArchitecture} onClick={onUpdateClick} loading={generatingArchitecture} variant={'normal'} iconName={'gen-ai'}>{updateButtonTitle}</Button>
                <Button disabled={descriptionInvalid || flowStepsInvalid || generatingArchitecture} onClick={onSaveClick} loading={savingArchitecture} variant={'primary'}>{saveButtonTitle}</Button>
            </SpaceBetween>
        ) :
        (
            <ButtonDropdown
                items={[
                    {
                        text: "Generate IaC project - WIP",
                        id: "generate-iac",
                        disabled: true
                    },
                    {
                        text: "Delete",
                        id: "delete-architecture"
                    }
                ]}
                mainAction={{text: updateButtonTitle, iconName: 'gen-ai', onClick: onUpdateClick, loading: generatingArchitecture}}
                variant="primary"
                onItemClick={onDropdownActionClick}
            />
        )
}


const ArchitectureContentLayout = ({canvasMode, architecture}) => {
    const auth = useAuth();
    const id = useParams().id;
    const dataStore = useArchitectureDataStore()
    const navigate = useNavigate();

    const [savedArchitectureId, setSavedArchitectureId] = useState(null)
    const [isSavingArchitecture, setIsSavingArchitecture] = useState(false)

    const [flowSteps, setFlowSteps] = useState(architecture?.interactionFlow?.length?.toString() ?? DEFAULT_FLOW_STEPS)
    const [architectureDescription, setArchitectureDescription] = useState(architecture?.description ?? '');
    const [isGeneratingArchitecture, setIsGeneratingArchitecture] = useState(ArchitectureUtils.isGeneratingArchitecture(architecture));
    const [deleteModalVisible, setDeleteModalVisible] = useState(false)
    const [isDeletingArchitecture, setIsDeletingArchitecture] = useState(false)

    useEffect(() => {
        setSavedArchitectureId(null)
    }, [id]);

    useEffect(() => {
        setIsGeneratingArchitecture(ArchitectureUtils.isGeneratingArchitecture(architecture))
        setArchitectureDescription(architecture?.description ?? '')
        setFlowSteps(`${architecture?.interactionFlow.length}` ?? DEFAULT_FLOW_STEPS)
    }, [architecture]);

    function generateArchitecture() {
        setIsGeneratingArchitecture(true)

        const params = {
            id: id,
            architectureDescription: architectureDescription,
            interactionFlowSteps: Number.parseInt(flowSteps)
        }

        sendRequest(Endpoint.generateArchitecture(auth.user.id_token, params))
            .then(handleResponse)
            .catch(() => setIsGeneratingArchitecture(false))
    }

    function saveArchitecture() {
        setSavedArchitectureId(null)
        setIsSavingArchitecture(true)

        sendRequest(Endpoint.putArchitecture(auth.user.id_token, architecture))
            .then(handleResponse)
            .then((response) => {
                dataStore.upsertItem(response)
                setSavedArchitectureId(response.id)
            })
            .catch(() => setIsGeneratingArchitecture(false))
            .finally(() => setIsSavingArchitecture(false));
    }

    function executeDropdownAction(event) {
        if (event.detail.id === 'delete-architecture') setDeleteModalVisible(true)
    }

    function deleteArchitecture() {
        setIsDeletingArchitecture(true)

        sendRequest(Endpoint.deleteArchitecture(auth.user.id_token, architecture?.id))
            .then(handleResponse)
            .then(() => {
                // Only go back if deleting a saved architecture, not clearing the canvas
                if (architecture?.id !== CANVAS_ID) {
                    dataStore.deleteById(architecture?.id)
                    navigate(-1)
                }
            })
            .catch(() => setIsDeletingArchitecture(false))
    }

    function clearCanvas() {
        sendRequest(Endpoint.deleteArchitecture(auth.user.id_token, architecture?.id))
        dataStore.deleteById(architecture?.id)
        navigate('/new-architecture', {replace: true})
    }

    return (
        <ContentLayout
            header={
                <Header variant={'h1'}
                        actions={
                            <HeaderActions
                                canvasMode={canvasMode}
                                generatingArchitecture={isGeneratingArchitecture}
                                savingArchitecture={isSavingArchitecture}
                                onUpdateClick={generateArchitecture}
                                onSaveClick={saveArchitecture}
                                onClearCanvasClick={() => clearCanvas()}
                                onDropdownActionClick={executeDropdownAction}
                                flowStepsInvalid={Number.parseInt(flowSteps) < MIN_FLOW_STEPS || Number.parseInt(flowSteps) > MAX_FLOW_STEPS}
                                descriptionInvalid={architectureDescription.length < MIN_DESCRIPTION_LENGTH}
                            />
                        }
                >
                    {architecture?.title}
                </Header>
            }
        >
            <SpaceBetween size={'l'} direction={'vertical'}>
                <ArchitectureSavedFlashbar archId={savedArchitectureId}/>

                {isGeneratingArchitecture ? (
                    <GeneratingArchitectureFlashbar />
                ) : architecture?.architectureError ? (
                    <ArchitectureGenerationErrorFlashbar message={architecture?.architectureError} />
                ) : null}

                <Configuration
                    description={architectureDescription}
                    flowSteps={flowSteps}
                    loading={isGeneratingArchitecture || isSavingArchitecture}
                    onDescriptionChange={(value) => setArchitectureDescription(value)}
                    onInteractionFlowChange={(value) => setFlowSteps(value)}
                    flowStepsInvalid={Number.parseInt(flowSteps) < MIN_FLOW_STEPS || Number.parseInt(flowSteps) > MAX_FLOW_STEPS}
                    descriptionInvalid={architectureDescription.length < MIN_DESCRIPTION_LENGTH}
                ></Configuration>

                {canvasMode?
                    <Architecture architecture={architecture} canvasMode={canvasMode}/> :
                    <Tabs
                        tabs={[
                            {
                                label: "Architecture diagram",
                                id: "first",
                                content: <Architecture architecture={architecture} canvasMode={canvasMode}/>
                            },
                            {
                                label: "Infrastructure as Code project",
                                id: "second",
                                content: <IaCProject architecture={architecture}/>
                            }
                        ]}
                    />
                }
            </SpaceBetween>

            <DeleteWithConfirmation
                visible={deleteModalVisible}
                title={'Delete saved architecture'}
                onDiscard={() => setDeleteModalVisible(false)}
                onConfirm={deleteArchitecture}
                isPerformingAction={isDeletingArchitecture}
                modalContent={"Permanently delete architecture? You can't undo this action."}
                alertContent={'This action will permanently delete this architecture from your saved list. Any previously generated IaC project will also be lost.'}
            />
        </ContentLayout>
    )
}


const LoadingContentLayout = () => (
    <ContentLayout>
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "100vh"
            }}
        >
            <Spinner size={'large'}></Spinner>
        </div>
    </ContentLayout>
)


export const Canvas = () => {
    const auth = useAuth();
    const id = useParams().id;
    const navigate = useNavigate()

    const canvasMode = id === CANVAS_ID
    const architecture = useArchitectureDataStore((state) => state.getById(id));

    const [isLoadingArchitecture, setIsLoadingArchitecture] = useState(false);

    useEffect(() => {
        if (architecture == null) getArchitecture(id);
    }, [id]);

    function getArchitecture() {
        setIsLoadingArchitecture(true)

        sendRequest(Endpoint.getArchitecture(auth.user.id_token, id))
            .then(handleResponse)
            .then(response => useArchitectureDataStore.getState().upsertItem(response))
            .catch((_) => navigate('/new-architecture', {replace: true}))
            .finally(() => setIsLoadingArchitecture(false));
    }

    return isLoadingArchitecture?
        <LoadingContentLayout/> :
        <ArchitectureContentLayout canvasMode={canvasMode} architecture={architecture}/>
}